import { gql, useMutation, useQuery } from '@apollo/client';
import { Link, graphql, navigate, useStaticQuery } from 'gatsby';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import BodyText from '@ui/atoms/BodyText';
import StyledLink from '@ui/atoms/StyledLink';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';
import Button from '@ui/atoms/Ws-Button';
import Eyebrow from '@ui/atoms/Ws-Eyebrow';

import theme from '@theme/WsTheme';

const PageWrapper = styled.div`
	flex-flow: column;
	background: ${theme.colors.lightBeige};
	position: relative;
	max-width: 120rem;
	margin-left: auto;
	margin-right: auto;
`;

const Headline = styled.div`
	font-size: ${theme.fontSizes.header.lg};
	padding: 2rem;
`;

const MetaLine = styled.div`
	font-size: ${theme.fontSizes.body.md};
	padding: 2rem;
`;

const HeaderContainer = styled.div`
	border-bottom: ${theme.colors.black} 0.2rem solid;
	width: 100%;
	text-align: center;
	padding: 3rem;
`;

const FooterContainer = styled.div`
	border-top: ${theme.colors.black} 0.2rem solid;
	width: 100%;
	padding-top: 3rem;
`;

const FootnotesContainer = styled.div`
	font-size: ${theme.fontSizes.body.sm};
	padding-left: 5rem;
	padding-right: 5rem;
`;

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: ${theme.spacing.md} ${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.lg};

	a {
		margin: 0 3rem 0 3rem;
		display: inline-flex;
		color: ${theme.colors.black};
		text-decoration: underline;
		flex-direction: column;
		align-items: center;
		text-align: center;
		@media ${theme.responsive.breakpoints.tablet} {
			flex-direction: row;
		}
	}
`;

const AgreementWrapper = styled.div`
	padding: 3rem 10rem 3rem 10rem;
`;

const AcceptButton = styled(Button)`
	width: 20rem;
`;

const DeclineButton = styled(Button)`
	width: 20rem;
	border: ${theme.colors.black} 2px solid;
	margin-right: ${theme.spacing.lg};
	background: ${theme.colors.white};

	${Eyebrow} {
		color: ${theme.colors.black};
	}
`;

interface IInnerspaceInterstitialProps {}

interface IFormFields {
	accepted: boolean;
}

const WAIVER_STATUS_QUERY = gql`
	query isAcceptanceCurrent {
		isAcceptanceCurrent(consentTypeIn: HIPAA_WAIVER)
	}
`;

const WAIVER_ACCEPTANCE_MUTATION = gql`
	mutation recordAcceptance {
		recordAcceptance(input: { consentTypeIn: HIPAA_WAIVER }) {
			clientMutationId
		}
	}
`;

const InnerspaceInterstitial: FC<IInnerspaceInterstitialProps> = () => {
	const { loading, error, data } = useQuery<{ isAcceptanceCurrent: boolean }>(WAIVER_STATUS_QUERY);

	const [acceptWaiver, { loading: acceptWaiverLoading, error: acceptWaiverError }] = useMutation(
		WAIVER_ACCEPTANCE_MUTATION
	);

	const [waiverDeclined, setWaiverDeclined] = useState(false);

	const {
		butterPage: { hipaa_waiver_content, hipaa_waiver_date, hipaa_waiver_footnotes },
	} = useStaticQuery(
		graphql`
			{
				butterPage(slug: { eq: "hipaa-waiver" }) {
					hipaa_waiver_footnotes
					hipaa_waiver_date
					hipaa_waiver_content
				}
			}
		`
	);

	const submitAcceptance = async () => {
		try {
			await acceptWaiver();
			navigate('/app/innerspace_nickname');
		} catch (e) {
			console.error('caught error when submitting acceptance: ' + e);
		}
	};

	const decline = async () => {
		setWaiverDeclined(true);
	};

	if (loading) {
		return <div>loading...</div>;
	}
	if (data?.isAcceptanceCurrent) {
		navigate('/app/innerspace_nickname');
		return (
			<PageWrapper>
				<HeaderContainer>
					<Headline>Redirecting...</Headline>
				</HeaderContainer>
			</PageWrapper>
		);
	} else {
		if (waiverDeclined) {
			return (
				<PageWrapper>
					<HeaderContainer>
						<Headline>You have declined HIPAA Authorization</Headline>
					</HeaderContainer>
					<AgreementWrapper>
						<MetaLine>
							Click the button below to return to the FOLX Home page. For assistance, please email{' '}
							<a href="mailto:folxsupport@folxhealth.com">FOLX Support</a>
						</MetaLine>
					</AgreementWrapper>
					<FooterContainer>
						<Footer>
							<DeclineButton label="Return to Home" onClick={() => navigate('/')} />
						</Footer>
					</FooterContainer>
				</PageWrapper>
			);
		} else {
			return (
				<PageWrapper>
					<HeaderContainer>
						<Headline>HIPAA Authorization</Headline>
						<MetaLine>Last Updated on {hipaa_waiver_date}</MetaLine>
					</HeaderContainer>
					<AgreementWrapper>
						<WYSIWYGContent dangerouslySetInnerHTML={{ __html: hipaa_waiver_content }} />
					</AgreementWrapper>
					<FooterContainer>
						<FootnotesContainer>{hipaa_waiver_footnotes}</FootnotesContainer>
						<Footer>
							<StyledLink
								variant="underline"
								to="https://www.mightynetworks.com/terms-of-use"
								target="mn">
								<BodyText size="xs">Mighty Networks Terms of Use</BodyText>
							</StyledLink>
							<StyledLink
								variant="underline"
								to="https://www.mightynetworks.com/privacy-policy"
								target="mn">
								<BodyText size="xs">Mighty Networks Privacy Policy</BodyText>
							</StyledLink>
						</Footer>
						<Footer>
							<DeclineButton label="I Decline" onClick={decline} />
							<AcceptButton label="I Accept" onClick={submitAcceptance} />
						</Footer>
					</FooterContainer>
				</PageWrapper>
			);
		}
	}
};
export default InnerspaceInterstitial;
